import { useCallback } from 'react';
import { App as AntdApp } from 'antd';

import { copyTextToClipboard } from '../../utils/clipboard';
import {
	COPY_LABEL,
	EXCLUDE_EMPTY_LABEL,
	EXCLUDE_LABEL,
	FILTER_EMPTY_LABEL,
	FILTER_LABEL,
} from './valueFilterOptions';

/**
 * The value menu's rows (KPR-1640, KPE-051 ruling i07), for the table cell's
 * menu and the read view's value menu alike. `model` is valueFilterOptions':
 * `{ entries, empty, copy }`.
 *
 *   one value        Filter by this value · Exclude this value · Copy value
 *   several values   a group per value, named by it, with the first two
 *   an empty cell    Filter by empty · Exclude empty
 *
 * Copy value copies the whole cell as shown, so it stands alone after the
 * groups. Keys: `filter:<i>`, `exclude:<i>`, `filter-empty`, `exclude-empty`,
 * `copy`. They are Record-group actions (platform-standards section 3): one
 * group, no danger row, nothing confirmed.
 */
export function valueMenuItems(model) {
	if (!model) return [];
	const items = [];
	const { entries = [], empty = null, copy = '' } = model;
	if (entries.length === 1) {
		const [entry] = entries;
		items.push({ key: 'filter:0', label: FILTER_LABEL });
		if (entry.exclude) items.push({ key: 'exclude:0', label: EXCLUDE_LABEL });
	} else {
		entries.forEach((entry, index) => {
			const children = [{ key: `filter:${index}`, label: FILTER_LABEL }];
			if (entry.exclude) children.push({ key: `exclude:${index}`, label: EXCLUDE_LABEL });
			items.push({ type: 'group', key: `value:${index}`, label: entry.label, children });
		});
	}
	if (empty) {
		items.push({ key: 'filter-empty', label: FILTER_EMPTY_LABEL });
		if (empty.exclude) items.push({ key: 'exclude-empty', label: EXCLUDE_EMPTY_LABEL });
	}
	if (copy) {
		if (items.length) items.push({ type: 'divider', key: 'divider-copy' });
		items.push({ key: 'copy', label: COPY_LABEL });
	}
	return items;
}

/** The filter a menu key stands for, or null (Copy, or a key the model does not hold). */
export function filterForKey(model, key) {
	if (!model || typeof key !== 'string') return null;
	if (key === 'filter-empty') return model.empty?.include || null;
	if (key === 'exclude-empty') return model.empty?.exclude || null;
	const match = /^(filter|exclude):(\d+)$/.exec(key);
	if (!match) return null;
	const entry = model.entries?.[Number(match[2])];
	return (match[1] === 'filter' ? entry?.include : entry?.exclude) || null;
}

/** Select an element's text, the fallback when the clipboard refuses. */
function selectContents(element) {
	try {
		const selection = window.getSelection?.();
		if (!selection || !element) return false;
		const range = document.createRange();
		range.selectNodeContents(element);
		selection.removeAllRanges();
		selection.addRange(range);
		return true;
	} catch {
		return false;
	}
}

/**
 * Runs a menu key: a filter goes to the page's door (`apply`), Copy value goes
 * to the clipboard and says "Copied" - or, when the browser refuses it, selects
 * the text where it stands (`selectEl`) and says how to copy it.
 */
export function useValueActionRunner({ apply }) {
	const { message } = AntdApp.useApp();
	return useCallback(async (model, key, selectEl = null) => {
		if (key === 'copy') {
			const text = model?.copy || '';
			if (!text) return;
			if (await copyTextToClipboard(text)) {
				message.success('Copied');
			} else if (selectContents(selectEl)) {
				message.info('Selected. Press Ctrl+C or ⌘C to copy.');
			} else {
				message.error('Copying is blocked here. Select the text and copy it.');
			}
			return;
		}
		const filter = filterForKey(model, key);
		if (filter && typeof apply === 'function') apply(filter);
	}, [apply, message]);
}
